// Restaurants reachable on cruise itineraries, grouped by destination id.
import aDoJoao from "@/assets/restaurants/a-do-joao.jpg.asset.json";
import aSardinha from "@/assets/restaurants/a-sardinha.jpg.asset.json";
import bordaDoCais from "@/assets/restaurants/borda-do-cais.jpg.asset.json";
import nosoloAgua from "@/assets/restaurants/nosolo-agua.jpg.asset.json";
import oSueste from "@/assets/restaurants/o-sueste.jpg.asset.json";
import pontoDeEncontro from "@/assets/restaurants/ponto-de-encontro.jpg.asset.json";
import praiaDaCoelha from "@/assets/restaurants/praia-da-coelha.jpg.asset.json";
import praiaGrandeAlvor from "@/assets/restaurants/praia-grande-alvor.jpg.asset.json";

export interface Restaurant {
  id: string;
  name: string;
  place: string;
  cuisine: { en: string; pt: string };
  rating?: string;
  summary: { en: string; pt: string };
  photo: string;
}

export const restaurantsByDestination: Record<string, Restaurant[]> = {
  alvor: [
    {
      id: "praia-grande-alvor",
      name: "Praia Grande",
      place: "Alvor, Algarve",
      cuisine: { en: "Portuguese · Seafood · Grilled fish", pt: "Portuguesa · Marisco · Peixe grelhado" },
      rating: "4.4/5.0",
      summary: {
        en: "A scenic restaurant in Alvor with open views over the coastline. The panoramic setting and welcoming atmosphere make it an easy place for a long lunch after a morning at sea.",
        pt: "Restaurante panorâmico em Alvor, com vistas abertas sobre a costa. O enquadramento e o ambiente acolhedor tornam-no ideal para um almoço demorado depois de uma manhã no mar.",
      },
      photo: praiaGrandeAlvor.url,
    },
  ],
  "praia-da-rocha": [
    {
      id: "nosolo-agua",
      name: "NosoloÁgua Portimão",
      place: "Marina de Portimão",
      cuisine: { en: "Seafood · Mediterranean · Sushi", pt: "Marisco · Mediterrânica · Sushi" },
      rating: "4.4/5.0",
      summary: {
        en: "A vibrant beach club at Portimão Marina with seafood, meat, Mediterranean plates, sushi and Portuguese specialities, ocean views and regular live music.",
        pt: "Beach club animado na Marina de Portimão, com marisco, carne, pratos mediterrânicos, sushi e especialidades portuguesas, vista de mar e música ao vivo.",
      },
      photo: nosoloAgua.url,
    },
    {
      id: "o-sueste",
      name: "O Sueste",
      place: "Ferragudo, Algarve",
      cuisine: { en: "Seafood · Grilled fish", pt: "Marisco · Peixe grelhado" },
      rating: "4.3/5.0",
      summary: {
        en: "Renowned in Ferragudo for the fresh catch — a wide choice of fish and shellfish, meat also available — eaten overlooking the calm waters of the Rio Arade.",
        pt: "Conhecido em Ferragudo pelo peixe fresco — grande escolha de peixe e marisco, carne também disponível — servido com vista para as águas calmas do Rio Arade.",
      },
      photo: oSueste.url,
    },
    {
      id: "borda-do-cais",
      name: "A Borda do Cais",
      place: "Ferragudo, Algarve",
      cuisine: { en: "Traditional Portuguese · Seafood · Grilled fish", pt: "Portuguesa tradicional · Marisco · Peixe grelhado" },
      rating: "4.4/5.0",
      summary: {
        en: "In the fishing village of Ferragudo, by the Rio Arade with Portimão across the water. A spacious conservatory and terrace seating, and a view that makes the meal.",
        pt: "Na aldeia piscatória de Ferragudo, à beira do Rio Arade com Portimão do outro lado. Amplo jardim de inverno e esplanada, e uma vista que faz a refeição.",
      },
      photo: bordaDoCais.url,
    },
  ],
  coelha: [
    {
      id: "praia-da-coelha",
      name: "Praia da Coelha",
      place: "Praia da Coelha, Albufeira",
      cuisine: { en: "Seafood · Portuguese cuisine", pt: "Marisco · Cozinha portuguesa" },
      rating: "4.3/5.0",
      summary: {
        en: "A family-run restaurant on Coelha beach serving dishes straight from the sea, meat also available — an idyllic place to relax after a morning along the coast.",
        pt: "Restaurante familiar na praia da Coelha, com pratos vindos do mar e também carne — um lugar idílico para descansar depois de uma manhã ao longo da costa.",
      },
      photo: praiaDaCoelha.url,
    },
  ],
  arrifes: [
    {
      id: "a-sardinha",
      name: "A Sardinha",
      place: "Praia dos Arrifes, Albufeira",
      cuisine: { en: "Traditional Portuguese · Seafood · Grilled fish", pt: "Portuguesa tradicional · Marisco · Peixe grelhado" },
      rating: "4.2/5.0",
      summary: {
        en: "Guests pick their own fish from the day's display, meat also available. Rocks, golden sand and blue sea make it one of the finest seafood settings in Portugal.",
        pt: "Os clientes escolhem o próprio peixe na banca do dia, com carne também disponível. Rochas, areia dourada e mar azul fazem dele um dos melhores cenários de marisco em Portugal.",
      },
      photo: aSardinha.url,
    },
  ],
  farol: [
    {
      id: "a-do-joao",
      name: "À-Do-João",
      place: "Ilha do Farol, Ria Formosa",
      cuisine: { en: "Traditional Portuguese · Seafood", pt: "Portuguesa tradicional · Marisco" },
      summary: {
        en: "A rustic island restaurant known for grilled fish and seafood — clams à Bulhão Pato, seafood rice, local ingredients, eaten a few steps from the water.",
        pt: "Restaurante rústico da ilha, conhecido pelo peixe grelhado e marisco — amêijoas à Bulhão Pato, arroz de marisco, ingredientes locais, a poucos passos da água.",
      },
      photo: aDoJoao.url,
    },
  ],
  cabanas: [
    {
      id: "ponto-de-encontro",
      name: "Ponto de Encontro",
      place: "Tavira (town), Algarve — not in Cabanas",
      cuisine: { en: "Seafood · Mediterranean", pt: "Marisco · Mediterrânica" },
      rating: "4.3/5.0",
      summary: {
        en: "Located in the town of Tavira — not in Cabanas — a short transfer inland from the waterfront. Modern Algarvian cooking: fresh shellfish and grilled octopus by the river, a long-standing favourite of shellfish lovers in the east.",
        pt: "Fica na vila de Tavira — não em Cabanas — a curta distância da frente de água. Cozinha algarvia moderna: marisco fresco e polvo grelhado junto ao rio, há muito uma referência para os amantes de marisco do sotavento.",
      },
      photo: pontoDeEncontro.url,
    },
  ],
};